import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { CroutonTimer, useEffectOnce } from "@swiggy-private/core-ui";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";

import { TimerPillWrapper } from "./TimerPill.style";

const TIMER_TEXT_FORMAT = "{0}m : {1}s";
const TIMER_STORAGE_KEY_PREFIX = "magic_items_timer_end_";

/**
 * Builds the full storage key for a given storageKey
 */
const getFullStorageKey = (storageKey: string): string => {
    return `${TIMER_STORAGE_KEY_PREFIX}${storageKey}`;
};

/**
 * Reads the remaining time from sessionStorage (pure read, no side effects)
 * Returns null if no stored value exists
 */
const getStoredRemainingTime = (storageKey: string | undefined): number | null => {
    if (!storageKey) {
        return null;
    }

    try {
        const storedEndTime = sessionStorage.getItem(getFullStorageKey(storageKey));

        if (!storedEndTime) {
            return null;
        }

        const endTimestamp = parseInt(storedEndTime, 10);
        const remainingMs = endTimestamp - Date.now();
        const remainingSeconds = Math.floor(remainingMs / 1000);

        return remainingSeconds;
    } catch {
        // sessionStorage might be unavailable
        return null;
    }
};

/**
 * Stores the end timestamp in sessionStorage
 */
const storeEndTimestamp = (storageKey: string | undefined, durationSeconds: number): void => {
    if (!storageKey || durationSeconds <= 0) {
        return;
    }

    try {
        const endTimestamp = Date.now() + durationSeconds * 1000;
        sessionStorage.setItem(getFullStorageKey(storageKey), endTimestamp.toString());
    } catch {
        // Ignore storage errors
    }
};

/**
 * Clears the timer storage
 */
const clearTimerStorage = (storageKey: string | undefined): void => {
    if (!storageKey) {
        return;
    }

    try {
        sessionStorage.removeItem(getFullStorageKey(storageKey));
    } catch {
        // Ignore storage errors
    }
};

/**
 * Calculates the effective duration by comparing API duration with stored duration.
 * Uses the minimum of stored remaining time and API duration
 * Returns 0 if timer has expired.
 */
const calculateEffectiveDuration = (storageKey: string | undefined, apiDuration: number): number => {
    const storedRemaining = getStoredRemainingTime(storageKey);

    // No stored value - use API duration
    if (storedRemaining === null) {
        return apiDuration;
    }

    // Timer expired in storage
    if (storedRemaining <= 0) {
        return 0;
    }

    // Use the minimum of stored remaining and API duration

    return Math.min(storedRemaining, apiDuration);
};

interface TimerPillProps {
    duration?: number;
    fillThreshold?: number;
    onTimerComplete?: VoidFunction;
    onTimerStart?: VoidFunction;
    onUrgencyTimeReached?: VoidFunction;
    onTimerExpired?: VoidFunction;
    defaultTextColor?: keyof IColors;
    defaultBgColor?: string;
    urgencyTextColor?: keyof IColors;
    urgencyBgColor?: keyof IColors;
    storageKey?: string; // Unique key for persisting timer
}

export const TimerPill: React.FC<TimerPillProps> = ({
    duration = 35,
    fillThreshold = 30,
    onTimerComplete,
    onTimerStart,
    onUrgencyTimeReached,
    onTimerExpired,
    defaultTextColor,
    defaultBgColor,
    urgencyTextColor,
    urgencyBgColor,
    storageKey,
}) => {
    const theme = useTheme();

    // Track if we've initialized storage to prevent StrictMode double-writes
    const hasInitializedStorage = useRef(false);
    // Track if we've already fired expired callbacks to prevent multiple firings
    const hasFiredExpiredCallbacks = useRef(false);
    // Track previous storageKey for cleanup
    const prevStorageKeyRef = useRef(storageKey);

    const effectiveDuration = useMemo(() => calculateEffectiveDuration(storageKey, duration), [storageKey, duration]);

    const isInitiallyInUrgency = effectiveDuration <= fillThreshold;
    const isInitiallyExpired = effectiveDuration <= 0;

    const [shouldFillRed, setShouldFillRed] = useState(isInitiallyInUrgency);
    const [remainingSeconds, setRemainingSeconds] = useState(effectiveDuration);
    const hasTriggeredFill = useRef(isInitiallyInUrgency);
    const skipFillAnimation = useRef(isInitiallyInUrgency);

    // Store end timestamp in useEffect (side effect, not during render)
    useEffect(() => {
        // Skip if already initialized (prevents StrictMode double-write)
        if (hasInitializedStorage.current) {
            return;
        }

        // Only store if we have a valid duration and it's not already expired
        if (storageKey && effectiveDuration > 0) {
            const storedRemaining = getStoredRemainingTime(storageKey);

            // Only write if no stored value exists (first time)
            if (storedRemaining === null) {
                storeEndTimestamp(storageKey, effectiveDuration);
            }

            hasInitializedStorage.current = true;
        }
    }, [storageKey, effectiveDuration]);

    // Cleanup old storage key when storageKey changes
    useEffect(() => {
        const prevKey = prevStorageKeyRef.current;

        if (prevKey && prevKey !== storageKey) {
            clearTimerStorage(prevKey);
            // Reset refs when storageKey changes
            hasInitializedStorage.current = false;
            hasFiredExpiredCallbacks.current = false;
        }

        prevStorageKeyRef.current = storageKey;
    }, [storageKey]);

    // Handle already expired timer
    useEffect(() => {
        if (isInitiallyExpired && !hasFiredExpiredCallbacks.current) {
            hasFiredExpiredCallbacks.current = true;

            onTimerComplete?.();
            onTimerExpired?.();
        }
    }, [isInitiallyExpired, onTimerComplete, onTimerExpired]);

    useEffectOnce(() => {
        if (!isInitiallyExpired) {
            onTimerStart?.();
        }
    });

    const handleTimeUpdate = useCallback(
        (remainingSecondsValue: number) => {
            setRemainingSeconds(remainingSecondsValue);

            if (remainingSecondsValue <= fillThreshold && !hasTriggeredFill.current) {
                setShouldFillRed(true);
                hasTriggeredFill.current = true;
                onUrgencyTimeReached?.();
            }

            if (remainingSecondsValue === 0) {
                // Clear persisted timer on expiry
                clearTimerStorage(storageKey);
                onTimerComplete?.();
                onTimerExpired?.();
            }
        },
        [fillThreshold, onTimerComplete, onUrgencyTimeReached, onTimerExpired, storageKey],
    );

    // Timer shows "0m : 0s" until backend confirms expiry

    const isInUrgencyRange = remainingSeconds <= fillThreshold;
    const timerTextColor = isInUrgencyRange ? urgencyTextColor : defaultTextColor;
    const showRipple = isInUrgencyRange && remainingSeconds > 0;

    return (
        <TimerPillWrapper
            $shouldFill={shouldFillRed}
            $showRipple={showRipple}
            $defaultBgColor={defaultBgColor}
            $urgencyBgColor={
                urgencyBgColor
                    ? theme.colors[theme.colorMode][urgencyBgColor]
                    : theme.colors[theme.colorMode].surprise_Deals_Primary
            }
            $skipFillAnimation={skipFillAnimation.current}
        >
            {showRipple && <div className="ripple-second" />}
            <CroutonTimer
                duration={effectiveDuration}
                textColor={timerTextColor as keyof IColors}
                timerText={TIMER_TEXT_FORMAT}
                onTimeUpdate={handleTimeUpdate}
                timerClassName="timer-pill-center"
            />
        </TimerPillWrapper>
    );
};
